import moment from 'moment'

import {Order} from '../../../../Pages/Order/Order.types'
import {isVariationCodeConfirmationExpired} from '../../../../Pages/Order/variationCode.utils'

/**
 * Checks if the order change request status matches any of the provided statuses.
 *
 * @param changeRequestStatus - The status from the API response
 * @param statuses - Array of statuses to check against
 * @returns true if the change request status matches any of the provided statuses
 */
export const checkOrderChangeRequestStatus = (
  changeRequestStatus: string | undefined,
  statuses: string[]
): boolean => {
  return changeRequestStatus ? statuses.includes(changeRequestStatus) : false
}

/**
 * Checks if an order has a pending change request (pending or sent).
 *
 * @param changeRequestStatus - The status from the API response
 * @returns true if there's a pending change request
 */
export const isOrderChangeRequestPending = (changeRequestStatus: string | undefined): boolean => {
  return checkOrderChangeRequestStatus(changeRequestStatus, ['pending', 'sent'])
}

/**
 * Checks if an order has a change request pending dispatch approval.
 *
 * @param changeRequestStatus - The status from the API response
 * @returns true if there's a change request pending approval
 */
export const isOrderChangeRequestPendingApproval = (
  changeRequestStatus: string | undefined
): boolean => {
  return checkOrderChangeRequestStatus(changeRequestStatus, ['pendingDispatchApproval'])
}

/**
 * Checks if the current time is before the order's maximum confirmation date.
 *
 * @param order - The order to check
 * @returns true if the current time is before orderConfirmableUntil
 */
export const isBeforeMaxConfirmationDate = (order: Order): boolean => {
  const orderConfirmableUntil = order.orderConfirmableUntil
    ? moment(order.orderConfirmableUntil)
    : undefined
  const now = moment()
  return !!orderConfirmableUntil && now.isBefore(orderConfirmableUntil)
}

/**
 * Determines if an order is confirmable by the user.
 *
 * An order is confirmable when:
 * 1. isOrderConfirmableByBusinessRules is true, AND
 * 2. The UE/CC rescheduled delivery time has not already passed
 *    (isVariationCodeConfirmationExpired is false), AND
 * 3. Either the current time is before the max confirmation date OR
 *    the order is pending manual approval (isConfirmablePendingManualApproval)
 *
 * @param order - The order to check
 * @returns true if the order can be confirmed
 */
export const isOrderConfirmable = (order: Order): boolean =>
  !!order.isOrderConfirmableByBusinessRules &&
  !isVariationCodeConfirmationExpired(order) &&
  (isBeforeMaxConfirmationDate(order) ||
    !!order.orderManagement?.isConfirmablePendingManualApproval)
